import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Star } from 'lucide-react';
const pageVariants = {
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.4
    }
  }
};
const fadeUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
const popularItems = [{
  id: 1,
  name: 'Lavender Honey Latte',
  desc: 'Espresso, steamed milk, house-made lavender syrup, local honey.',
  price: '$5.50',
  img: 'https://images.unsplash.com/photo-1541167760496-1628856ab772?auto=format&fit=crop&q=80&w=800'
}, {
  id: 2,
  name: 'Avocado Smash Toast',
  desc: 'Sourdough, smashed avocado, cherry tomatoes, microgreens, chili flakes.',
  price: '$9.00',
  img: 'https://images.unsplash.com/photo-1588137378633-dea1336ce1e2?auto=format&fit=crop&q=80&w=800'
}, {
  id: 3,
  name: 'Matcha Rose Croissant',
  desc: 'Flaky butter croissant filled with matcha cream and rose petals.',
  price: '$4.75',
  img: 'https://images.unsplash.com/photo-1530610476181-d83430b64dcd?auto=format&fit=crop&q=80&w=800'
}];
export function Home() {
  return <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full" data-id="element-254">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-10 overflow-hidden" data-id="element-255">
        <div className="absolute inset-0 z-0" data-id="element-256">
          <div className="absolute inset-0 bg-cafe-900/40 z-10" data-id="element-257"></div>
          <img src="https://images.unsplash.com/photo-1445116572660-236099ec97a0?auto=format&fit=crop&q=80&w=2000" alt="Café interior" className="w-full h-full object-cover" data-id="element-258" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 w-full" data-id="element-259">
          <div className="max-w-2xl" data-id="element-260">
            <motion.div variants={fadeUp} className="overflow-hidden mb-4" data-id="element-261">
              <span className="inline-block py-1 px-3 rounded-full bg-cafe-sage/90 text-white text-sm font-medium tracking-wider uppercase mb-4" data-id="element-262">
                Est. 2024
              </span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="font-serif text-5xl md:text-7xl font-bold text-white leading-tight mb-6" data-id="element-263">
              Where Every Cup <br data-id="element-264" />
              <span className="text-cafe-100 italic font-light" data-id="element-265">
                Tells a Story
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg md:text-xl text-cafe-100/90 mb-10 max-w-lg leading-relaxed" data-id="element-266">
              Experience artisanal coffee, freshly baked pastries, and a warm
              community atmosphere in the heart of the city.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4" data-id="element-267">
              <Link to="/menu" className="bg-cafe-sage text-white px-8 py-4 rounded-sm font-medium hover:bg-cafe-800 transition-all duration-300 flex items-center gap-2 group" data-id="element-268">
                View Menu
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-269" />
              </Link>
              <Link to="/about" className="bg-white/10 backdrop-blur-md border border-white/30 text-white px-8 py-4 rounded-sm font-medium hover:bg-white/20 transition-all duration-300" data-id="element-270">
                Our Story
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popular Picks */}
      <section className="py-24 bg-cafe-50" data-id="element-271">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-272">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-273">
            <h2 className="font-serif text-4xl font-bold text-cafe-900 mb-4" data-id="element-274">
              Popular Picks
            </h2>
            <p className="text-cafe-800/70 max-w-2xl mx-auto" data-id="element-275">
              Discover our most loved creations, crafted with passion and the
              finest ingredients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-276">
            {popularItems.map((item, i) => <motion.div key={item.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow group" data-id="element-277">
                <div className="h-64 overflow-hidden" data-id="element-278">
                  <img src={item.img} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-279" />
                </div>
                <div className="p-6" data-id="element-280">
                  <div className="flex justify-between items-start mb-2" data-id="element-281">
                    <h3 className="font-serif text-xl font-bold text-cafe-900" data-id="element-282">
                      {item.name}
                    </h3>
                    <span className="text-cafe-sage font-semibold" data-id="element-283">
                      {item.price}
                    </span>
                  </div>
                  <p className="text-cafe-800/70 text-sm leading-relaxed" data-id="element-284">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Story Teaser */}
      <section className="py-24 bg-cafe-900 text-cafe-100" data-id="element-285">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-286">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-287">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-288">
              <img src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&q=80&w=1000" alt="Coffee beans" className="rounded-lg shadow-xl" data-id="element-289" />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-290">
              <h2 className="font-serif text-4xl font-bold mb-6" data-id="element-291">
                Rooted in Passion
              </h2>
              <p className="text-cafe-100/80 mb-6 leading-relaxed" data-id="element-292">
                Brew & Bloom started with a simple idea: that coffee is more
                than just a morning routine. It's a craft, a moment of pause,
                and a way to bring people together.
              </p>
              <p className="text-cafe-100/80 mb-8 leading-relaxed" data-id="element-293">
                We source our beans ethically from sustainable farms and roast
                them locally to ensure every cup delivers an exceptional
                experience.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-cafe-sage hover:text-white transition-colors font-medium border-b border-cafe-sage pb-1" data-id="element-294">
                Read our full story
                <ArrowRight className="w-4 h-4" data-id="element-295" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-cafe-100" data-id="element-296">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-297">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-298">
            <div className="flex justify-center gap-1 mb-6 text-cafe-terra" data-id="element-299">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-6 h-6 fill-current" data-id="element-300" />)}
            </div>
            <blockquote className="font-serif text-2xl md:text-3xl text-cafe-900 italic leading-relaxed mb-8" data-id="element-301">
              "The atmosphere is incredibly warm, and their lavender honey latte
              is simply the best I've ever had. It's my new favorite spot to
              read and relax."
            </blockquote>
            <p className="text-cafe-800 font-medium uppercase tracking-widest text-sm" data-id="element-302">
              — Sarah Jenkins, Local Resident
            </p>
          </motion.div>
        </div>
      </section>
    </motion.div>;
}